<script setup lang="ts">
import { computed } from 'vue';

// Props pour contrôler l'affichage
const props = defineProps({
    variant: {
        type: String as () => 'banner' | 'inline' | 'compact',
        default: 'banner'
    }
});

// Dates de la campagne Noël (en heure de Paris)
const START_DATE = '2025-12-08T00:00:00';
const END_DATE = '2025-12-31T23:59:59';

// Check si on est en mode dev (pour forcer l'affichage)
const isDev = (import.meta as any).env?.DEV || false;

// Fonction pour obtenir la date/heure actuelle à Paris
function getParisTime(): Date {
    const now = new Date();
    // Convertir en heure de Paris (Europe/Paris)
    const parisTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
    return parisTime;
}

// Vérifier si on est dans la période de Noël
const isChristmasPeriod = computed(() => {
    // En dev, on affiche toujours pour tester
    if (isDev) {
        return true;
    }
    
    // En prod, on vérifie les dates en heure de Paris
    const nowParis = getParisTime();
    const startDate = new Date(START_DATE);
    const endDate = new Date(END_DATE);
    
    return nowParis >= startDate && nowParis <= endDate;
});
</script>

<template>
    <div v-if="isChristmasPeriod" class="christmas-promo" :class="variant">
        <div class="christmas-content">
            <h3 class="title">🎄 Noël 2025 🎁</h3>
            <p class="dates">Du 08.12.2025 au 31.12.2025</p>
            <p class="promo">
                <span class="discount">-10%</span> 
                <span class="with-code">dès 100€ avec le code</span>
                <span class="code">NOEL25</span>
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.christmas-promo {
    &.compact {
        width: 100%;
        background: linear-gradient(90deg, #0f4c2a 0%, #1a6b3d 100%);
        color: #fff;
        padding: 8px 15px;
        text-align: center;
        border-bottom: 2px solid #c41e3a;
        
        .christmas-content {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            flex-wrap: wrap;
        }
        
        .title {
            font-family: Montserrat, sans-serif;
            font-size: 14px;
            font-weight: 700;
            margin: 0;
            color: #FFD700;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        
        .dates {
            font-family: Montserrat, sans-serif;
            font-size: 12px;
            font-weight: 400;
            margin: 0;
            color: #fff;
        }
        
        .promo {
            font-family: Montserrat, sans-serif;
            font-size: 13px;
            font-weight: 600;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 6px;
            
            .discount {
                color: #FF6B6B;
                font-size: 16px;
                font-weight: 700;
            }
            
            .with-code {
                color: #fff;
                font-size: 11px;
            }
            
            .code {
                background: #c41e3a;
                color: #fff;
                padding: 2px 8px;
                border-radius: 3px;
                font-weight: 700;
                font-size: 13px;
                letter-spacing: 1px;
            }
        }
    }
    
    &.banner {
        width: 100%;
        background: linear-gradient(135deg, #0f4c2a 0%, #1a6b3d 100%);
        color: #fff;
        padding: 20px;
        text-align: center;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        margin: 20px 0;
        border: 2px solid #c41e3a;
        
        .christmas-content {
            max-width: 800px;
            margin: 0 auto;
        }
        
        .title {
            font-family: Montserrat, sans-serif;
            font-size: 32px;
            font-weight: 700;
            margin: 0 0 10px 0;
            color: #FFD700;
            text-transform: uppercase;
            letter-spacing: 2px;
        }
        
        .dates {
            font-family: Montserrat, sans-serif;
            font-size: 18px;
            font-weight: 500;
            margin: 0 0 15px 0;
            color: #fff;
        }
        
        .promo {
            font-family: Montserrat, sans-serif;
            font-size: 20px;
            font-weight: 600;
            margin: 0;
            
            .discount {
                color: #FF6B6B;
                font-size: 28px;
                font-weight: 700;
                margin-right: 8px;
            }
            
            .with-code {
                color: #fff;
                margin-right: 8px;
            }
            
            .code {
                background: #c41e3a;
                color: #fff;
                padding: 4px 12px;
                border-radius: 4px;
                font-weight: 700;
                font-size: 22px;
                letter-spacing: 1px;
            }
        }
    }
    
    &.inline {
        background: linear-gradient(135deg, #0f4c2a 0%, #1a6b3d 100%);
        color: #fff;
        padding: 15px;
        text-align: center;
        border-radius: 6px;
        border: 2px solid #c41e3a;
        margin: 10px 0;
        
        .christmas-content {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        
        .title {
            font-family: Montserrat, sans-serif;
            font-size: 18px;
            font-weight: 700;
            margin: 0;
            color: #FFD700;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        
        .dates {
            font-family: Montserrat, sans-serif;
            font-size: 12px;
            font-weight: 400;
            margin: 0;
            color: #fff;
        }
        
        .promo {
            font-family: Montserrat, sans-serif;
            font-size: 14px;
            font-weight: 600;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 4px;
            align-items: center;
            
            .discount {
                color: #FF6B6B;
                font-size: 22px;
                font-weight: 700;
            }
            
            .with-code {
                color: #fff;
                font-size: 12px;
            }
            
            .code {
                background: #c41e3a;
                color: #fff;
                padding: 3px 10px;
                border-radius: 4px;
                font-weight: 700;
                font-size: 16px;
                letter-spacing: 1px;
            }
        }
    }
}

// Responsive pour mobile
@media (max-width: 768px) {
    .christmas-promo {
        &.compact {
            padding: 6px 10px;
            
            .christmas-content {
                gap: 8px;
            }
            
            .title {
                font-size: 12px;
            }
            
            .dates {
                font-size: 10px;
            }
            
            .promo {
                font-size: 11px;
                
                .discount {
                    font-size: 14px;
                }
                
                .with-code {
                    font-size: 10px;
                }
                
                .code {
                    font-size: 11px;
                    padding: 2px 6px;
                }
            }
        }
        
        &.banner {
            padding: 15px;
            margin: 15px 0;
            
            .title {
                font-size: 24px;
            }
            
            .dates {
                font-size: 14px;
            }
            
            .promo {
                font-size: 16px;
                
                .discount {
                    font-size: 22px;
                }
                
                .code {
                    font-size: 18px;
                }
            }
        }
    }
}
</style>
